const SMHI_BASE_URL =
  "https://opendata-download-metfcst.smhi.se/api/category/snow1g/version/1";

const WEATHER_PARAMETERS = [
  "air_temperature",
  "wind_speed",
  "air_pressure_at_mean_sea_level",
  "precipitation_amount_mean",
  "symbol_code",
].join(",");

export function createFallbackWeatherData(errorMessage) {
  return {
    parsedData: {
      rainAmt: 0,
      snowAmt: 0,
      cloudAmt: 0,
      windSpeed: 0,
    },
    weatherCode: 0,
    weatherTemperature: null,
    windSpeed: null,
    airPressure: null,
    meanPrecipitation: null,
    timestamp: Date.now(),
    isFallback: true,
    error: errorMessage,
  };
}

export async function fetchWeatherData(longitude, latitude) {
  const smhiURL = `${SMHI_BASE_URL}/geotype/point/lon/${longitude}/lat/${latitude}/data.json?timeseries=3&parameters=${WEATHER_PARAMETERS}`;
  const response = await fetch(smhiURL);

  if (!response.ok) {
    throw new Error("SMHI API call failed with status: " + response.status);
  }

  const data = await response.json();
  const currentData = data.timeSeries?.[0]?.data;

  if (!currentData) {
    throw new Error("SMHI API returned no forecast data");
  }

  return {
    parsedData: parseWeatherData(data),
    weatherCode: currentData.symbol_code ?? 0,
    weatherTemperature: currentData.air_temperature ?? null,
    windSpeed: currentData.wind_speed ?? null,
    airPressure: currentData.air_pressure_at_mean_sea_level ?? null,
    meanPrecipitation: currentData.precipitation_amount_mean ?? null,
    timestamp: Date.now(),
    isFallback: false,
  };
}

function parseWeatherData(data) {
  const timeSeries = data.timeSeries?.slice(0, 3) ?? [];
  const weatherSymbols = timeSeries
    .map((timeStep) => timeStep.data?.symbol_code)
    .filter((symbol) => Number.isFinite(symbol));

  let rainAmt = 0; //(0-3)
  let snowAmt = 0; //(0-3)
  let cloudAmt = 0; //(0-6, where 5 and 6 are overcast/foggy)

  weatherSymbols.forEach((symbol) => {
    switch (symbol) {
      case 1: // Clear sky
        break;
      case 2: // Nearly clear sky
        cloudAmt = Math.max(cloudAmt, 1);
        break;
      case 3: // Variable cloudiness
        cloudAmt = Math.max(cloudAmt, 2);
        break;
      case 4: // Halfclear sky
        cloudAmt = Math.max(cloudAmt, 3);
        break;
      case 5: // Cloudy sky
        cloudAmt = Math.max(cloudAmt, 4);
        break;
      case 6: // Overcast
        cloudAmt = Math.max(cloudAmt, 5);
        break;
      case 7: // Fog
        cloudAmt = Math.max(cloudAmt, 6);
        break;
      case 8: // Light rain showers
      case 18: // Light rain
        rainAmt = Math.max(rainAmt, 1);
        break;
      case 9: // Moderate rain showers
      case 19: // Moderate rain
        rainAmt = Math.max(rainAmt, 2);
        break;
      case 10: // Heavy rain showers
      case 20: // Heavy rain
      case 11: // Thunderstorm
      case 21: // Thunder
        rainAmt = Math.max(rainAmt, 3);
        break;
      case 12: // Light sleet showers
      case 13: // Moderate sleet showers
      case 14: // Heavy sleet showers
      case 22: // Light sleet
      case 23: // Moderate sleet
      case 24: // Heavy sleet
        rainAmt = Math.max(rainAmt, 1);
        break;
      case 15: // Light snow showers
      case 25: // Light snowfall
        snowAmt = Math.max(snowAmt, 1);
        break;
      case 16: // Moderate snow showers
      case 26: // Moderate snowfall
        snowAmt = Math.max(snowAmt, 2);
        break;
      case 17: // Heavy snow showers
      case 27: // Heavy snowfall
        snowAmt = Math.max(snowAmt, 3);
        break;
      default:
        break;
    }
  });

  const maxWindSpeed = timeSeries.reduce((max, timeStep) => {
    const windSpeed = timeStep.data?.wind_speed;
    return Number.isFinite(windSpeed) ? Math.max(max, windSpeed) : max;
  }, 0);

  return {
    rainAmt,
    snowAmt,
    cloudAmt,
    windSpeed: Math.min(maxWindSpeed, 30) / 30,
  };
}
